import { Middleware } from "redux"
import { AppDispatch, AppRootState, AppThunk } from "./appStore"
import { RootRouteContentType } from "@fusion-advantage/formlib"
import { appError } from "./errorSlice"
import { AppFormRelayInterface, serializeFusionError } from "@fusion-advantage/backcompat"
import { appFormSave } from "../lib/persistence/appFormSave"
import { err, ok, Result } from "neverthrow"
import { setBackgroundDraftSaveTimeoutId } from "./backgroundSlice"
import { AppFormRelayImpl } from "@fusion-advantage/formrender"

// attempt background save of form draft (when changes are pending)
// every 10 seconds
const BACKGROUND_DRAFT_SAVE_INTERVAL = 10000

export const backgroundDraftSaveMw: Middleware<{}, AppRootState> = store => {
	const dispatch = store.dispatch as AppDispatch
	return next => action => {
		const prevState = store.getState()
		const prevContent = prevState.app.content
		const result = next(action)
		const nextState = store.getState()
		const nextContent = nextState.app.content
		if(prevContent !== nextContent) {
			dispatch(cancelFutureBackgroundDraftSave())
			if(nextContent.type === RootRouteContentType.Form) {
				dispatch(scheduleFutureBackgroundDraftSave())
			}
		}
		return result
	}
}

interface backgroundDraftSaveMwTickArgs {
	reschedule: boolean
}

const backgroundDraftSaveMwTick = ({reschedule}: backgroundDraftSaveMwTickArgs): AppThunk<Promise<void>> => async (dispatch, getState, { renderInstances }) => {
	const state = getState()
	const content = state.app.content
	if(content.type !== RootRouteContentType.Form) {
		// we don't expect this state, because the middleware code above
		// should have detected any content change away from a form already
		// and not rescheduled this
		dispatch(appError({
			err: serializeFusionError('backgroundDraftSaveMwTick', `expected form content type but got '${content.type}'`)
		}))
		// do not reschedule
		return
	}
	const lastSavedAt = getState().background.formLastBackgroundDraftSaveAt
	let nextSavedAt: string
	const rShouldSave: Result<AppFormRelayImpl, string> = (() => {
		// avoid saving background drafts while the form is being explicitly saved
		if(state.render.importLoading) {
			return err('currently already saving the form')
		}
		const instance = renderInstances.get(content.renderInstanceId)
		const appFormRelay = instance?.appFormRelay
		// it is possible that the render instance hasn't been created yet
		if(!appFormRelay) {
			return err('appFormRelay is not available')
		}
		// there must be a newer user-chosen change
		// (use the interface here as that type accurately reflects that
		// getLastChosenAt is not present on legacy renderers)
		const relayInterface: AppFormRelayInterface = appFormRelay
		if(relayInterface.getLastChosenAt) {
			// for newer form renderers we can check if there are updates
			// using getLastChosenAt
			const rGetLast = relayInterface.getLastChosenAt()
			const lastChosenAt = rGetLast.result?.lastChosenAt
			if(!lastChosenAt) {
				return err('user has not updated the form')
			}
			nextSavedAt = lastChosenAt
		} else {
			// adaptation for legacy renderer support
			const lastRequestAt = getState().background.formLastLegacyDraftSaveRequestAt
			if(!lastRequestAt) {
				return err('renderer has not requested a draft save')
			}
			nextSavedAt = lastRequestAt
		}
		if(lastSavedAt === nextSavedAt) {
			return err('saved draft already up to date')
		}
		return ok(appFormRelay)
	})()
	if(rShouldSave.isOk()) {
		await dispatch(appFormSave({
			appFormRelay: rShouldSave.value,
			// if the form is not in a saveable mode, silently ignore
			ignoreDraftProposalError: true,
			closeOnSuccess: false,
		}))
	}
	if(reschedule) {
		dispatch(scheduleFutureBackgroundDraftSave())
	}
}

const scheduleFutureBackgroundDraftSave = (): AppThunk<void> => (dispatch) => {
	const timeoutId = setTimeout(() => dispatch(backgroundDraftSaveMwTick({reschedule: true})), BACKGROUND_DRAFT_SAVE_INTERVAL)
	dispatch(setBackgroundDraftSaveTimeoutId(timeoutId))
}

const cancelFutureBackgroundDraftSave = (): AppThunk<void> => (dispatch, getState) => {
	const timeoutId = getState().background.draftSaveTimeoutId
	if(timeoutId) {
		clearTimeout(timeoutId)
		dispatch(setBackgroundDraftSaveTimeoutId(null))
	}
}
